// src/ATPModal.js
import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { supabase } from './supabaseClient';

const REGIONS = [
  { key: 'Dakar',       label: 'Dakar / Rufisque / Banlieue', deplacement: 0 },
  { key: 'Thies',      label: 'Thiès',                       deplacement: 8000 },
  { key: 'Diourbel',    label: 'Diourbel',                    deplacement: 15000 },
  { key: 'Fatick',      label: 'Fatick',                      deplacement: 15000 },
  { key: 'Kaolack',     label: 'Kaolack',                     deplacement: 20000 },
  { key: 'Kaffrine',    label: 'Kaffrine',                    deplacement: 25000 },
  { key: 'Louga',       label: 'Louga',                       deplacement: 25000 },
  { key: 'Saint-Louis', label: 'Saint-Louis',                 deplacement: 35000 },
  { key: 'Matam',       label: 'Matam',                       deplacement: 45000 },
  { key: 'Tambacounda', label: 'Tambacounda',                 deplacement: 40000 },
  { key: 'Kolda',       label: 'Kolda',                       deplacement: 45000 },
  { key: 'Sedhiou',    label: 'Sédhiou',                     deplacement: 45000 },
  { key: 'Ziguinchor',  label: 'Ziguinchor',                  deplacement: 50000 },
  { key: 'Kedougou',   label: 'Kédougou',                    deplacement: 55000 },
];

const ATP_TYPES = [
  { key: 'particulier', label: 'Particulier', prix: 35000 },
  { key: 'entreprise',  label: 'Entreprise',  prix: 65000 },
];

const PROJET_TYPES = [
  { key: 'residentiel', label: 'Résidentiel',         ref: 'ATP-RES',
    desc: (ref, region) => 'Audit Technique Préalable - Système solaire résidentiel\nRef : ' + ref + ' | Site : ' + region + '\nVisite sur site, relevé de consommation, analyse charges, dimensionnement personnalisé\nModalité spéciale : Remboursé intégralement en cas de commande ferme' },
  { key: 'entreprise',  label: 'Entreprise / Industrie', ref: 'ATP-ENT',
    desc: (ref, region) => 'Audit Technique Préalable - Installation solaire professionnelle\nRef : ' + ref + ' | Site : ' + region + '\nAudit énergétique, analyse charges industrielles, dimensionnement optimisé, étude de rentabilité\nModalité spéciale : Remboursé intégralement en cas de commande ferme' },
  { key: 'pompe',       label: 'Pompage solaire',      ref: 'ATP-PMP',
    desc: (ref, region) => 'Audit Technique Préalable - Kit pompage solaire\nRef : ' + ref + ' | Site : ' + region + '\nAnalyse forage/puits, mesure HMT réelle, débit requis, dimensionnement kit pompe solaire\nModalité spéciale : Remboursé intégralement en cas de commande ferme' },
  { key: 'pv',          label: 'Système PV / Hybride', ref: 'ATP-PV',
    desc: (ref, region) => 'Audit Technique Préalable - Système photovoltaïque\nRef : ' + ref + ' | Site : ' + region + '\nRelevé site, analyse ombrage, étude orientation toiture, optimisation production PV\nModalité spéciale : Remboursé intégralement en cas de commande ferme' },
];

const fmt = (n) => Math.round(n).toString().replace(/\B(?=(\d{3})+(?!\d))/g, '\u00a0') + ' FCFA';

const ATPModal = ({ onClose, onSelect }) => {
  const [region, setRegion] = useState(REGIONS[0]);
  const [atpType, setAtpType] = useState(ATP_TYPES[0]);
  const [projetType, setProjetType] = useState(PROJET_TYPES[0]);
  const [nextNum, setNextNum] = useState(1);
  const [regionsDB, setRegionsDB] = useState(REGIONS);
  const [atpTypesDB, setAtpTypesDB] = useState(ATP_TYPES);

  useEffect(() => {
    supabase.from('settings').select('valeur').eq('cle', 'atp_counter').single()
      .then(({ data }) => { if (data) setNextNum(parseInt(data.valeur || '0') + 1); });

    // Charger prix ATP depuis catalogue_produits
    supabase.from('catalogue_produits').select('ref,nom,prix').in('ref', ['ATP-PART', 'ATP-ENT']).eq('actif', true)
      .then(({ data }) => {
        if (data && data.length > 0) {
          const part = data.find(p => p.ref === 'ATP-PART');
          const ent = data.find(p => p.ref === 'ATP-ENT');
          setAtpTypesDB([
            { key: 'particulier', label: 'Particulier', prix: part ? part.prix : 35000 },
            { key: 'entreprise',  label: 'Entreprise',  prix: ent  ? ent.prix  : 65000 },
          ]);
          setAtpType({ key: 'particulier', label: 'Particulier', prix: part ? part.prix : 35000 });
        }
      });

    // Charger prix deplacements depuis catalogue_produits
    supabase.from('catalogue_produits').select('ref,nom,prix').like('ref', 'DEPL-%').eq('actif', true).order('prix')
      .then(({ data }) => {
        if (data && data.length > 0) {
          const map = {};
          data.forEach(d => { map[d.ref] = d.prix; });
          setRegionsDB([
            { key: 'Dakar',       label: 'Dakar / Rufisque / Banlieue', deplacement: map['DEPL-DAKAR']       ?? 0 },
            { key: 'Thies',      label: 'Thiès',                       deplacement: map['DEPL-THIES']       ?? 8000 },
            { key: 'Diourbel',    label: 'Diourbel',                    deplacement: map['DEPL-DIOURBEL']    ?? 15000 },
            { key: 'Fatick',      label: 'Fatick',                      deplacement: map['DEPL-FATICK']      ?? 15000 },
            { key: 'Kaolack',     label: 'Kaolack',                     deplacement: map['DEPL-KAOLACK']     ?? 20000 },
            { key: 'Kaffrine',    label: 'Kaffrine',                    deplacement: map['DEPL-KAFFRINE']    ?? 25000 },
            { key: 'Louga',       label: 'Louga',                       deplacement: map['DEPL-LOUGA']       ?? 25000 },
            { key: 'Saint-Louis', label: 'Saint-Louis',                 deplacement: map['DEPL-SAINTLOUIS']  ?? 35000 },
            { key: 'Matam',       label: 'Matam',                       deplacement: map['DEPL-MATAM']       ?? 45000 },
            { key: 'Tambacounda', label: 'Tambacounda',                 deplacement: map['DEPL-TAMBACOUNDA'] ?? 40000 },
            { key: 'Kolda',       label: 'Kolda',                       deplacement: map['DEPL-KOLDA']       ?? 45000 },
            { key: 'Sedhiou',    label: 'Sédhiou',                     deplacement: map['DEPL-SEDHIOU']     ?? 45000 },
            { key: 'Ziguinchor',  label: 'Ziguinchor',                  deplacement: map['DEPL-ZIGUINCHOR']  ?? 50000 },
            { key: 'Kedougou',   label: 'Kédougou',                    deplacement: map['DEPL-KEDOUGOU']    ?? 55000 },
          ]);
        }
      });
  }, []);

  const prixATP = atpType.prix;
  const prixDepl = region.deplacement;
  const total = prixATP + prixDepl;
  const today = new Date();
  const dd = String(today.getDate()).padStart(2, '0');
  const mm = String(today.getMonth() + 1).padStart(2, '0');
  const yy = today.getFullYear();
  const ref = 'ATP-' + yy + '-' + dd + mm + '-' + projetType.ref.replace('ATP-', '');

  const handleInserer = async () => {
    // Incrementer compteur
    await supabase.from('settings').update({ valeur: String(nextNum) }).eq('cle', 'atp_counter');

    const items = [];

    // Titre (non facture)
    items.push({
      _titre: true,
      description: 'AUDIT TECHNIQUE PRÉÉLABLE (ATP) | ' + projetType.label.toUpperCase() + ' | ' + region.label.toUpperCase() + ' | Ref : ' + ref,
      quantity: 1,
      unitPrice: 0,
    });

    // Ligne ATP principale
    items.push({
      description: projetType.desc(ref, region.label),
      quantity: 1,
      unitPrice: prixATP,
    });

    // Déplacement si hors Dakar
    if (prixDepl > 0) {
      items.push({
        description: 'Déplacement sur site - ' + region.label,
        quantity: 1,
        unitPrice: prixDepl,
      });
    }

    onSelect(items);
    onClose();
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 1000, padding: '16px', fontFamily: 'Segoe UI, sans-serif'
    }}>
      <div style={{
        background: 'white', borderRadius: '16px', width: '100%', maxWidth: '580px',
        maxHeight: '90vh', overflow: 'auto', boxShadow: '0 8px 40px rgba(0,0,0,0.2)'
      }}>
        {/* Header */}
        <div style={{
          background: 'linear-gradient(135deg, #78350f, #92400e)',
          padding: '20px 24px', borderRadius: '16px 16px 0 0',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between'
        }}>
          <div>
            <h2 style={{ color: 'white', fontSize: '18px', fontWeight: 700, margin: 0 }}>
              Audit Technique Préalable (ATP)
            </h2>
            <p style={{ color: 'rgba(255,255,255,0.8)', fontSize: '12px', margin: '4px 0 0' }}>
              {ref}
            </p>
          </div>
          <button onClick={onClose} style={{
            background: 'rgba(255,255,255,0.2)', border: 'none', color: 'white',
            borderRadius: '8px', padding: '8px', cursor: 'pointer', display: 'flex', alignItems: 'center'
          }}>
            <X size={18} />
          </button>
        </div>

        <div style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '18px' }}>

          {/* Type de projet */}
          <div>
            <label style={{ fontSize: '13px', fontWeight: 600, color: '#374151', display: 'block', marginBottom: '8px' }}>
              Type de projet
            </label>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '8px' }}>
              {PROJET_TYPES.map(p => (
                <button key={p.key} onClick={() => setProjetType(p)}
                  style={{
                    padding: '10px 14px', borderRadius: '8px', cursor: 'pointer', textAlign: 'left',
                    border: projetType.key === p.key ? '2px solid #78350f' : '2px solid #e5e7eb',
                    background: projetType.key === p.key ? '#fef3c7' : 'white', transition: 'all 0.15s'
                  }}>
                  <div style={{ fontWeight: 600, fontSize: '12px', color: projetType.key === p.key ? '#78350f' : '#374151' }}>
                    {p.label}
                  </div>
                  <div style={{ fontSize: '10px', color: '#9ca3af', marginTop: '2px' }}>{p.ref}-XXXX</div>
                </button>
              ))}
            </div>
          </div>

          {/* Tarif */}
          <div>
            <label style={{ fontSize: '13px', fontWeight: 600, color: '#374151', display: 'block', marginBottom: '8px' }}>
              Tarif ATP
            </label>
            <div style={{ display: 'flex', gap: '8px' }}>
              {atpTypesDB.map(t => (
                <button key={t.key} onClick={() => setAtpType(t)}
                  style={{
                    flex: 1, padding: '10px', borderRadius: '8px', cursor: 'pointer',
                    border: atpType.key === t.key ? '2px solid #78350f' : '2px solid #e5e7eb',
                    background: atpType.key === t.key ? '#fef3c7' : 'white',
                    transition: 'all 0.15s', textAlign: 'center'
                  }}>
                  <div style={{ fontWeight: 700, fontSize: '12px', color: atpType.key === t.key ? '#78350f' : '#374151' }}>
                    {t.label}
                  </div>
                  <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '2px' }}>{fmt(t.prix)}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Region */}
          <div>
            <label style={{ fontSize: '13px', fontWeight: 600, color: '#374151', display: 'block', marginBottom: '8px' }}>
              Région d'intervention
            </label>
            <select value={region.key}
              onChange={e => setRegion(regionsDB.find(r => r.key === e.target.value))}
              style={{ width: '100%', padding: '10px 12px', border: '1px solid #e5e7eb',
                borderRadius: '8px', fontSize: '13px', outline: 'none', background: 'white' }}>
              {regionsDB.map(r => (
                <option key={r.key} value={r.key}>
                  {r.label}{r.deplacement > 0 ? ' - Dépl. : ' + fmt(r.deplacement) : ' - Déplacement inclus'}
                </option>
              ))}
            </select>
          </div>

          {/* Récapitulatif */}
          <div style={{ background: '#f9fafb', borderRadius: '10px', padding: '16px', border: '1px solid #e5e7eb' }}>
            <div style={{ fontWeight: 700, fontSize: '13px', color: '#374151', marginBottom: '10px' }}>
              Récapitulatif — {ref}
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', marginBottom: '6px' }}>
              <span style={{ color: '#6b7280' }}>ATP {atpType.label} — {projetType.label}</span>
              <span style={{ fontWeight: 600 }}>{fmt(prixATP)}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', marginBottom: '6px' }}>
              <span style={{ color: '#6b7280' }}>Déplacement — {region.label}</span>
              <span style={{ fontWeight: 600 }}>{prixDepl === 0 ? 'Inclus' : fmt(prixDepl)}</span>
            </div>
            <div style={{ borderTop: '1px solid #e5e7eb', marginTop: '8px', paddingTop: '8px',
              display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ fontWeight: 700, color: '#374151', fontSize: '14px' }}>Total ATP</span>
              <span style={{ fontWeight: 800, color: '#78350f', fontSize: '16px' }}>{fmt(total)}</span>
            </div>
          </div>

          {/* Bouton inserer */}
          <button onClick={handleInserer}
            style={{ padding: '14px', background: '#78350f', color: 'white', border: 'none',
              borderRadius: '10px', fontWeight: 700, fontSize: '14px', cursor: 'pointer' }}
            onMouseEnter={e => e.currentTarget.style.background = '#92400e'}
            onMouseLeave={e => e.currentTarget.style.background = '#78350f'}>
            Insérer l'ATP dans le devis
          </button>
        </div>
      </div>
    </div>
  );
};

export default ATPModal;
